/**
 * Phone number utilities for E.123 format
 * Storage: +14182331234 (E.123 international format)
 * Display: 418-233-2332 (NANP format for +1 numbers)
 */

/**
 * Normalize phone number to E.123 format (+1XXXXXXXXXX)
 * Defaults to +1 country code for Canada/USA
 */
export function normalizePhoneNumber(input: string): string {
  // Remove all non-digit characters
  const digits = input.replace(/\D/g, '');

  // If it starts with 1 and has 11 digits (1XXXXXXXXXX)
  if (digits.startsWith('1') && digits.length === 11) {
    return `+${digits}`;
  }

  // If it has 10 digits, assume +1 (North America)
  if (digits.length === 10) {
    return `+1${digits}`;
  }

  // If it has 11+ digits and doesn't start with 1, assume international with country code
  if (digits.length >= 11) {
    return `+${digits}`;
  }

  // Less than 10 digits or malformed, return as-is with + prefix if not empty
  return digits ? `+${digits}` : '';
}

/**
 * Format phone number for display
 * +14182331234 → 418-233-2332
 * +447700900123 → +44 7700 900123 (non-NANP)
 * Handles various input formats robustly
 */
export function formatPhoneForDisplay(phone: string): string {
  if (!phone) return '';

  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');

  // If empty after cleaning, return empty
  if (!digits) return '';

  // Handle +1 prefix (11 digits starting with 1)
  if (digits.startsWith('1') && digits.length === 11) {
    const areaCode = digits.slice(1, 4);
    const prefix = digits.slice(4, 7);
    const lineNumber = digits.slice(7, 11);
    return `${areaCode}-${prefix}-${lineNumber}`;
  }

  // Handle 10 digits (assume North America, no country code)
  if (digits.length === 10) {
    const areaCode = digits.slice(0, 3);
    const prefix = digits.slice(3, 6);
    const lineNumber = digits.slice(6, 10);
    return `${areaCode}-${prefix}-${lineNumber}`;
  }

  // For other country codes (more than 10 digits, not starting with 1)
  if (digits.length > 10) {
    const countryCode = digits.slice(0, digits.length - 10);
    const rest = digits.slice(digits.length - 10);
    const part1 = rest.slice(0, 3);
    const part2 = rest.slice(3, 6);
    const part3 = rest.slice(6);
    return `+${countryCode} ${part1} ${part2} ${part3}`;
  }

  // Fallback: if less than 10 digits or unusual format, return with basic formatting
  if (digits.length >= 7) {
    const prefix = digits.slice(0, 3);
    const lineNumber = digits.slice(3);
    return `${prefix}-${lineNumber}`;
  }

  // Last resort: return as-is
  return phone;
}
